import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { ArticleModel } from 'src/app/models/archive/article.model';
import { PhysicalInventoryDetailModel } from 'src/app/models/inventory/physical-inventory-detail.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CountTableModalModel } from 'src/app/models/tables/inventory/count-table-modal.model';
import { PhysicalInventoryArticleResponseTable } from 'src/app/models/tables/inventory/physical-inventory-article-response-table.model';
import { PhysicalInventoryDetailRegisterTable } from 'src/app/models/tables/inventory/physical-inventory-detail-register-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PhysicalInventoryDetailService {
  private readonly _httpClient = inject(HttpClient);

  createPhysicalInventoryDetail(physicalInventoryDetailContract: PhysicalInventoryDetailModel): Observable<PhysicalInventoryDetailModel> {
    return this._httpClient.post<PhysicalInventoryDetailModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.CreatePhysicalInventoryDetail}`,
      physicalInventoryDetailContract
    );
  }

  createSomeDetails(details: Array<PhysicalInventoryDetailModel>): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.CreateSomeDetails}`, details);
  }

  deletePhysicalInventoryDetail(physicalInventoryDetailId: number): Observable<PhysicalInventoryDetailModel> {
    return this._httpClient.delete<PhysicalInventoryDetailModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.DeletePhysicalInventoryDetail}/${physicalInventoryDetailId}`
    );
  }

  deleteSomeDetails(physicalInventoryDetailIds: Array<number>): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.DeleteSomeDetailsAsync}`,
      physicalInventoryDetailIds);
  }

  getArticleByCodeAsync(physicalInventoryId: number, articleCode: number): Observable<ArticleModel> {
    return this._httpClient.get<ArticleModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.GetArticleByCodeAsync}/${physicalInventoryId}/${articleCode}`);
  }

  getAllPhysicalInventoryDetails(): Observable<PhysicalInventoryDetailModel[]> {
    return this._httpClient.get<PhysicalInventoryDetailModel[]>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.GetAllPhysicalInventoryDetails}`
    );
  }

  getColumnsForCountModal(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.GetColumnsForCountModal}`);
  }

  getColumnsForListArticleSelection(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.GetColumnsForListArticleSelection}`);
  }

  getColumnsForRegisterTable(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.GetColumnsForRegisterTable}`);
  }

  getListArticleSelection(physicalInventoryId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<PhysicalInventoryArticleResponseTable>> {
    const params = new HttpParams().set('physicalInventoryId', physicalInventoryId).set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<PhysicalInventoryArticleResponseTable>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.GetListArticleSelectionAsync}`,
      filters, { params: params }
    );
  }

  getArticleCountModalWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, physicalInventoryId: number, count: number): Observable<PagedResponseModel<CountTableModalModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '')
      .set('ascendant', ascendant).set('physicalInventoryId', physicalInventoryId).set('count', count);

    return this._httpClient.get<PagedResponseModel<CountTableModalModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.GetArticleCountModalWithPaginationAsync}`,
      { params: params }
    );
  }

  getPhysicalInventoryDetailByKeys(physicalInventoryId: number, articleId: number, count: number): Observable<PhysicalInventoryDetailModel> {
    return this._httpClient.get<PhysicalInventoryDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.GetPhysicalInventoryDetailByKeys}/${physicalInventoryId}/${articleId}/${count}`);
  }

  getPhysicalInventoryDetailById(physicalInventoryDetailId: number): Observable<PhysicalInventoryDetailModel> {
    return this._httpClient.get<PhysicalInventoryDetailModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.GetPhysicalInventoryDetailById}/${physicalInventoryDetailId}`
    );
  }

  getPhysicalinventoryDetailsForRegisterTable(page: number, limit: number, columnSort: string | null, ascendant: boolean,
    physicalInventoryId: number, filters: ColumnResponseModel[]): Observable<PagedResponseModel<PhysicalInventoryDetailRegisterTable>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<PhysicalInventoryDetailRegisterTable>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.GetPhysicalInventoryDetailsForRegisterTable}/${physicalInventoryId}`,
      filters, { params: params }
    );
  }

  updatePhysicalInventoryDetail(physicalInventoryDetailContract: PhysicalInventoryDetailModel): Observable<PhysicalInventoryDetailModel> {
    return this._httpClient.put<PhysicalInventoryDetailModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.physicalInventoryDetail.UpdatePhysicalInventoryDetail}`,
      physicalInventoryDetailContract
    );
  }
}
